import lodash from "lodash";
import { reactive, toRaw } from "vue";
import i18n from "../../../../i18n";
import store from "../../../../store/index";
import { Col, Row } from "ant-design-vue";

import {
    checkAttribute,
    initUseForm,
    onClickActive,
    onClickActiveAll,
    onClickArchiveAll,
    onClickCreate,
    onClickDeleteAll,
    onClickEdit,
    onClickView,
} from "../../../../utils/form";
import { createLoading } from "../../../../utils/loading";
import {
    createAppPageHeaderTop,
    createManageActive,
    createManageArchive,
    createManageDelete,
    createManageExportCsv,
    createManageSelect,
    createPage,
    createRowActionEdit,
    createRowActionView,
    onClickReport
} from "../../../../utils/page";
import { required, requiredWhen, validateError } from "../../../../utils/validate";

import { createConditionAction, createConditionForm } from "./request.condition";
import { createColumns, createFields, createInformPaymentFields } from "./request.section";
import { has, matchMenu } from "../../../../utils/menu-context";
import { useForm as createUseForm } from "@ant-design-vue/use";
import { setAppPageHeader } from "../../../../components/layout/app-layout/app-layout.logic";
import {
    createButton,
    createButtonActive,
    createButtonCreate,
    createButtonEdit,
    createButtonSave,
    createButtonView
} from "../../../../utils/button";
import { setIsLoading as setIsLoadingApp } from "../../../../app.logic";
import { openModalForm as openModalFormConfirmRequest } from "../../license-request/modal/confirm-request.logic";
import { createWhereFor as createWhereForLicenseRequest } from "../../license-request/request/request.logic";
import { default as notification } from "ant-design-vue/lib/notification";

export const { isLoading, setIsLoading } = createLoading();
export const actionPrefix = "resource/v1/transaction/licenseRequest";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/save`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/active`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/delete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/activeAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/deleteAll`, { ids, config: { params: { isHardDelete: true } } });
export const callChanges = ({ id, criteria }) => store.dispatch(`${actionPrefix}/changes`, { id, criteria });
export const callRequestLicense = ({ form }) => store.dispatch(`${actionPrefix}/requestLicense`, { data: form });
export const callReportReceipt = ({ data }) => store.dispatch(`${actionPrefix}/reportReceipt`, { data });
export const callReportReceiptUrl = ({ data }) => store.dispatch(`${actionPrefix}/reportReceiptUrl`, { data });

export const onSearch = ({ props, page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    const customWhere = lodash.cloneDeep(where);
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where: [...customWhere, ...createWhereFor({ props })], sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSave = ({ props, data, refs, formParams }) => {
    const { viewOnly, routerPrefix } = props;
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            let isRequireDuty = false;
            let isRequireFee = false;
            data.useForm.modelRef?.feeStatements?.forEach(data => {
                if (data.refType?.key == "duty") {
                    isRequireDuty =  true;
                }
                if (data.refType?.key == "fee") {
                    isRequireFee = true
                }
            })
            if (isRequireDuty) {
                const isAttachDuty = data.useForm.modelRef.attachmentDutySlips.filter(it => it.isDelete != true)?.length > 0;
                if (!isAttachDuty) {
                    setIsLoadingApp(false);
                    notification["error"]({ message: i18n.global.t("views.feeStatement.error.requireDutySlipAttachment") });
                    return;
                }
                isRequireDuty = false;
            }
            if (isRequireFee) {
                const isAttachFee =  data.useForm.modelRef.attachmentFeeSlips.filter(it => it.isDelete != true)?.length > 0;
                if (!isAttachFee) {
                    setIsLoadingApp(false);
                    notification["error"]({ message: i18n.global.t("views.feeStatement.error.requireFeeSlipAttachment") });
                    return;
                }
                isRequireFee = false;
            }
            if (!isRequireDuty && !isRequireFee) {
                return callSave({ form: { ...data.useForm.modelRef, ...formParams } })
                    .finally(() => setIsLoadingApp(false))
                    .then(record => {
                        if (viewOnly) initForm({ props, data, refs });
                        else onClickView({ record, routerPrefix });
                    });
            }
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickRequest = ({ props, data, refs, formParams, successText, otp, isSendRequest }) => {
    const { viewOnly, routerPrefix } = props;
    setIsLoadingApp(true);
    return callRequestLicense({ form: { licenseRequest: { ...data.useForm.modelRef, ...formParams }, otp, isSendRequest } })
        .finally(() => setIsLoadingApp(false))
        .then(record => {
            if (successText) notification["success"]({ message: successText });
            if (viewOnly) initForm({ props, data, refs });
            else onClickView({ record, routerPrefix });
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ props, id, forLog }) => {
    if (!forLog) {
        return {
            select: [
                "**", "refRequester.**"
            ],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }, ...createWhereFor({ props })],
            children: [
                {
                    name: "feeStatements",
                    select: ["**"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }],
                    children: []
                },
                {
                    name: "attachmentFeeSlips",
                    select: ["**"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }],
                    children: []
                },
                {
                    name: "attachmentDutySlips",
                    select: ["**"],
                    page: 0,
                    size: 1000000,
                    sort: [{ name: "createDate", type: "ASC" }],
                    children: []
                },
            ]
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createWhereFor = ({ props }) => {
    const menuContextRequest = matchMenu("/app/license-requests", store.state.resource.auth.info.menus);
    return [
        { name: "refStatus.key", type: "String", operator: "=", value: "approve" },
        { name: "refPaymentStatus.key", type: "String", operator: "in", value: ["waitPayment", "waitApprovePayment", "approvePayment", "cancel"] },
        ...createWhereForLicenseRequest({ props, menuContextRequest }),
    ];
};

export const createForm = () => {
    return {
        id: null,
        attachmentFeeSlips: [],
        attachmentDutySlips: []
    };
};

export const createRules = ({ key , data }) => {
    // const modelRef = data.useForm?.modelRef || data.form;
    // const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {
        [`${prefix}id`]: [required],
        [`${prefix}refPaymentMethod`]: [required],
        [`${prefix}refPaymentBank`]: [required],
        [`${prefix}refBankProvince`]: [required],
        [`${prefix}payFeeAtDate`]: [requiredWhen({ when: () => data.isFee })],
        [`${prefix}payDutyAtDate`]: [requiredWhen({ when: () => data.isDuty })],
    };

    // model?.attachmentFeeSlips.forEach((item, index) => {
    //     if (item.isDelete !== true) {
    //         rules[`${prefix}attachmentFeeSlips.${index}.size`] = [required];
    //     }
    // });
    //
    // model?.attachmentDutySlips.forEach((item, index) => {
    //     if (item.isDelete !== true) {
    //         rules[`${prefix}attachmentDutySlips.${index}.size`] = [required];
    //     }
    // });

    return rules

};

export const createFormTitle = ({ data }) => {
    const modelRef = data.useForm.modelRef;
    return modelRef.id ? `${i18n.global.t("views.feeStatement.title")} ${modelRef.requestCode} (${modelRef.refPaymentStatus?.value})` : i18n.global.t("views.feeStatement.title");
};

export const initData = ({ props, data, refs }) => {
    const { itemsKey } = props
    const { useForm } = data
    const { modelRef } = useForm
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef
    data.fields = createFields({ props, data, refs, isLoading });
    data.informPaymentFields = createInformPaymentFields({ props, data, refs, isLoading });
    data.page = createPage({ data: items })
    data.tableOptions = { search: { activeKey: null } }
    data.manages = []
    data.manageExtras = []
    data.rowActions = []
}

export const initForm = ({ props, data, refs }) => {
    const { routerPrefix } = props;
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createFields({ props, data, refs, isLoading });
    data.informPaymentFields = createInformPaymentFields({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });

    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { menuContext, viewOnly, routerPrefix } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    const canWaitApprovePayment = state.conditionAction.canWaitApprovePayment({ props, record: initialModel });
    const canApprove = state.conditionAction.canApprove({ props, record: initialModel });
    const canReportReceipt = state.conditionAction.canReportReceipt({ props, record: initialModel });

    if (id && has({ menuContext, key: "canCreate" })) {
        col1.push(createButtonCreate({ onClick: () => onClickCreate({ routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && id && has({ menuContext, key: "canView" })) {
        col1.push(createButtonView({ onClick: () => onClickView({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (viewOnly && id && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(createButtonSave({ onClick: () => onClickSave({ props, data, refs }), classStyle: "m-1" }));
    }
    if (viewOnly && id && !isActive && has({ menuContext, key: "canActive" })) {
        col2.push(
            createButtonActive({
                onClick: () =>
                    onClickActive({
                        record: { id },
                        callActive,
                        setIsLoading: setIsLoadingApp,
                        onClickEdit: () => onClickEdit({ record: { id }, routerPrefix })
                    }),
                classStyle: "m-1"
            })
        );
    }
    if (viewOnly && (id && isActive) && canWaitApprovePayment) {
        const waitPayment = data.paymentStatuses.find(status => status.key === "waitPayment");
        col2.push(
            createButton({
                text: `${waitPayment?.attribute?.actionText}`,
                classStyle: `m-1 ${waitPayment?.attribute?.btnClass}`,
                onClick: () => {
                    data.isSendRequest = true
                    openModalFormConfirmRequest({
                        props: { ...props, modalMessage: i18n.global.t("views.request.licenseRequest.modal.confirmInformPayment")} ,
                        data,
                        onOk: () => {
                            onClickRequest({
                                props,
                                data,
                                refs,
                                otp: null,
                                isSendRequest: data.isSendRequest,
                                successText: i18n.global.t("views.notification.successText", { status: waitPayment?.attribute?.actionText })
                            });
                        }
                    });
                },
            })
        )
    }


    if (viewOnly && (id && isActive) && canApprove && modelRef.isApprovePayment) {
        const waitApprovePayment = data.paymentStatuses.find(status => status.key === "waitApprovePayment");
        col2.push(
            createButton({
                text: `${waitApprovePayment?.attribute?.actionText}`,
                classStyle: `m-1 ${waitApprovePayment?.attribute?.btnClass}`,
                onClick: () => {
                    data.isSendRequest = true
                    openModalFormConfirmRequest({
                        props: { ...props, modalMessage: i18n.global.t("views.request.licenseRequest.modal.confirmApprovePayment")} ,
                        data,
                        onOk: () => {
                            onClickRequest({
                                props,
                                data,
                                refs,
                                otp: null,
                                isSendRequest: data.isSendRequest,
                                successText: i18n.global.t("views.feeStatement.approvePaymentSuccess")
                            });
                        }
                    });
                },
            })
        )
    }

    /* ปุ่มแจ้งแก้ไข */
    if (viewOnly && (id && isActive) && canApprove && modelRef.isRejectPayment) {
        col2.push(
            createButton({
                text: i18n.global.t("button.rejectPayment"),
                classStyle: `m-1 btn-warning`,
                onClick: () => {
                    data.isSendRequest = true
                    openModalFormConfirmRequest({
                        props: { ...props, modalMessage: i18n.global.t("views.request.licenseRequest.modal.confirmRejectPayment")} ,
                        data,
                        onOk: () => {
                            onClickRequest({
                                props,
                                data,
                                refs,
                                otp: null,
                                isSendRequest: data.isSendRequest,
                                successText: i18n.global.t("views.feeStatement.approvePaymentSuccess")
                            });
                        }
                    });
                },
            })
        )
    }



    if (viewOnly && (id && isActive) && canReportReceipt && modelRef.refPaymentStatus?.key == "approvePayment") {
        col1.push(
            createButton({
                text: i18n.global.t("views.feeStatement.reportReceipt"),
                classStyle: `m-1 btn-primary`,
                onClick: () => {
                    onClickReport({
                        callReport: () => callReportReceipt({ data: { licenseRequest: { id: id } } }),
                        fileName: `receipt_${modelRef.requestCode}.pdf`
                    });
                },
            })
        );
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { form } = props;
    const { id, useForm } = data;
    const { modelRef } = useForm;
    let promise = null;
    if (form) {
        promise = Promise.resolve({ data: [form] });
    } else if (id) {
        promise = callSearch({ criteria: createSearchOneCriteria({ props, id }) });
    } else {
        promise = Promise.resolve({ data: [useForm.modelRef] });
    }
    return promise
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const callLicenseRequestStatus = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "LICENSE_REQUEST_STATUS", value: searchQuery });
export const callPaymentStatus = ({ searchQuery }) => store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "PAYMENT_STATUS", value: searchQuery });


export const loadDefaultForm = ({ data }) => {
    const promises = [
        callLicenseRequestStatus({}),
        callPaymentStatus({}),
    ];
    return Promise.all(promises).then(arr => {
        data.statuses = arr[0];
        data.paymentStatuses = arr[1];
        data.isFee =  data.useForm?.modelRef?.feeStatements.find(it => it.refType.key == "fee") != null;
        data.isDuty =  data.useForm?.modelRef?.feeStatements.find(it => it.refType.key == "duty") != null;
    })
};

export const initState = ({ props }) => {
    const { menuContext, routerPrefix } = props;
    state.manages = [
        createManageSelect({ menuContext }),
        createManageActive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickActiveAll({ rowSelection, callActiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") })
        }),
        createManageArchive({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickArchiveAll({ rowSelection, callArchiveAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canArchive({ props, record }) })
        }),
        createManageDelete({
            menuContext,
            onClick: ({ rowSelection }) =>
                onClickDeleteAll({ rowSelection, callDeleteAll, state, onSearch: search => onSearch({ props, ...search }), setIsLoading }),
            confirm: ({ rowSelection }) => ({ content: rowSelection.selectedRows.map(d => d.draftCode).join(", ") }),
            getCheckboxProps: ({ record }) => ({ disabled: !state.conditionAction.canDelete({ props, record }) })
        }),
        createManageExportCsv({ menuContext })
    ];
    state.manageExtras = [];
    state.rowActions = [
        createRowActionView({ menuContext, onClick: ({ record }) => onClickView({ record, routerPrefix }) }),
        createRowActionEdit({
            menuContext,
            onClick: ({ record }) => onClickEdit({ record, routerPrefix }),
            visible: ({ record }) => state.conditionAction.canEdit({ props, record })
        }),
    ];
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({
        options: {
            select: [
                "**",
                "refReceiveDept.*",
                "refVesselRequest.code",
            ],
            isDistinct: false,
            sort: [{ name: "approveDate", type: "DESC" }]
        }
    }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});